import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { defaultHomeFor } from "@/components/ProtectedRoute";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Zap, ShieldCheck, ShoppingBag } from "lucide-react";

const FEATURES = [
  { icon: Zap,         title: "Sin vueltas",  text: "Elegí, sumá, envialo. Tu pedido entra al instante." },
  { icon: ShoppingBag, title: "Catálogo claro", text: "Fotos, precios y stock al día. Nada de copy-paste de WhatsApp." },
  { icon: ShieldCheck, title: "Tu historial",  text: "Seguí cada pedido, descargá el remito cuando esté listo." },
];

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user.role) {
      navigate(defaultHomeFor(user.role), { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (!r.ok) {
      setErr(r.error || "Email o contraseña incorrectos");
      return;
    }
    const dest = location.state?.from || defaultHomeFor(r.user.role);
    navigate(dest, { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-paper text-[#1A1A1A]">
      {/* Left brand panel (desktop) */}
      <aside className="hidden lg:flex flex-col justify-between w-1/2 bg-[#2C2A26] text-[#F9F6F0] px-14 py-12 relative overflow-hidden">
        {/* warm glow accents */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#C85A32]/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-[#D49A36]/8 blur-3xl pointer-events-none" />

        <div className="flex items-center gap-4 relative">
          <div className="h-20 w-20 rounded-2xl bg-white p-2 shadow-xl ring-1 ring-white/20">
            <img src="/logo.png" alt="Sandwichs Lo De Dani" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[20px] tracking-tight">Sandwichs Lo De Dani</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#D49A36] font-medium">Pedidos online</div>
          </div>
        </div>

        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D49A36] font-semibold mb-3">
            La tienda de tu sandwichería
          </div>
          <h1 className="font-display font-bold text-5xl leading-[1.05] mb-5 tracking-tight">
            Pedí tus sánguches<br />
            <span className="text-[#D49A36]">rápido, claro</span><br />
            y sin vueltas.
          </h1>
          <p className="text-[#C9C2B4] max-w-md text-[15px] leading-relaxed">
            Elegí productos del catálogo, armá tu pedido y seguí su estado desde un solo lugar.
            Sin planillas. Sin mensajes confusos. Sin esperas.
          </p>

          <div className="mt-10 space-y-4 max-w-md">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-white/5 text-[#D49A36] flex items-center justify-center shrink-0 ring-1 ring-white/10">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-display font-semibold text-[14px] text-[#F9F6F0]">{title}</div>
                  <div className="text-[13px] text-[#A39A87] leading-snug">{text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[12px] text-[#8C8474] relative">© Sandwichs Lo De Dani — hecho con cariño.</div>
      </aside>

      {/* Right form */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-[420px]">
          <div className="flex items-center gap-3 lg:hidden mb-8">
            <div className="h-14 w-14 rounded-xl bg-white p-1.5 shadow-md">
              <img src="/logo.png" alt="" className="h-full w-full object-contain" />
            </div>
            <div className="font-display font-bold text-[16px]">Sandwichs Lo De Dani</div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C85A32] font-semibold mb-2">
            Bienvenido
          </div>
          <h2 className="font-display font-bold text-4xl mb-2 tracking-tight">Ingresá</h2>
          <p className="text-[#595959] mb-8">Usá las credenciales que te pasamos por WhatsApp.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[12px] font-semibold text-[#595959] mb-1.5 block">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="h-11 bg-white"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[12px] font-semibold text-[#595959] mb-1.5 block">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 bg-white pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  aria-label="Mostrar/ocultar contraseña"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#8C8C8C] hover:text-[#1A1A1A]"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {err && (
              <div className="text-sm text-[#8E2E2E] bg-[#FBEBEB] border border-[#F0CECE] rounded-lg p-3" data-testid="login-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#C85A32] hover:bg-[#A64A29] text-white font-semibold rounded-lg"
              data-testid="login-submit"
            >
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>

          <div className="mt-10 text-[12px] text-[#8C8C8C]">
            ¿Sos cliente y no tenés cuenta? Pedile a tu vendedor que te cree una.
          </div>
        </div>
      </main>
    </div>
  );
}
